import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AlertTitle,
  Divider
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Save,
  Cancel,
  Groups,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  const [anneeActive, setAnneeActive] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    short: '',
    actif: true,
    niveau_id: '',
    filiere_id: '',
    regime: 'NORMAL'
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadAll = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const [cls, niv, fil, fn, ann] = await Promise.all([
        axios.get('/api/classes', { headers }),
        axios.get('/api/niveaux', { headers }),
        axios.get('/api/filieres', { headers }),
        axios.get('/api/filiere-niveaux', { headers }),
        axios.get('/api/annees', { headers })
      ]);
      setClasses(cls.data || []);
      setNiveaux(niv.data || []);
      setFilieres(fil.data || []);
      setFiliereNiveaux(fn.data || []);
      const active = (ann.data || []).find(a => a.estActive);
      setAnneeActive(active ? String(active.annee_id) : '');
      setError('');
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Cette action est réservée aux administrateurs.");
      } else {
        setError('Impossible de charger les classes');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const getNiveau = (niveauId) => niveaux.find(n => String(n.niveau_id) === String(niveauId));
  const getFiliere = (filiereId) => filieres.find(f => String(f.filiere_id) === String(filiereId));

  // L annee active est prioritaire : les niveaux des autres annees ne servent pas a
  // programmer l annee en cours, et les melanger menait a afficher les matieres de
  // l annee precedente.
  const niveauxAnneeActive = () => {
    const base = anneeActive
      ? niveaux.filter(n => {
          const na = n.annee_id === undefined || n.annee_id === null ? '' : String(n.annee_id);
          return na === anneeActive || na === '';
        })
      : niveaux;
    return base.slice().sort((a, b) => String(a.intitule).localeCompare(String(b.intitule)));
  };

  const filieresAnneeActive = () =>
    (anneeActive ? filieres.filter(f => String(f.annee_id) === anneeActive) : filieres)
      .slice()
      .sort((a, b) => String(a.libelle || a.intitule || '').localeCompare(String(b.libelle || b.intitule || '')));

  // Un niveau appartient a une filiere via filiereNiveaux. Choisir la filiere d abord
  // evite de proposer un niveau incoherent avec elle.
  const niveauxDeFiliere = (filiereId) => {
    if (!filiereId) return [];
    const ids = filiereNiveaux
      .filter(fn => String(fn.filiere_id) === String(filiereId))
      .map(fn => String(fn.niveau_id));
    return niveauxAnneeActive().filter(n => ids.includes(String(n.niveau_id)));
  };

  // Une classe sans niveau est le cas dangereux : l application devine alors le niveau d apres
  // le nom, et deux niveaux portent le meme intitule pour deux annees (IRM2). On l affiche en
  // tete pour qu elle ne passe pas inapercue.
  const sansLien = classes.filter(c => !c.niveau_id).length;

  const filtered = classes.filter(c => {
    const t = searchTerm.toLowerCase();
    const niveau = getNiveau(c.niveau_id);
    const filiere = getFiliere(c.filiere_id);
    return (
      String(c.name || '').toLowerCase().includes(t) ||
      String(c.short || '').toLowerCase().includes(t) ||
      String(niveau?.intitule || '').toLowerCase().includes(t) ||
      String(filiere?.libelle || '').toLowerCase().includes(t)
    );
  });

  const handleOpenAdd = () => {
    setModalMode('add');
    setSelectedItem(null);
    setFormData({ name: '', short: '', actif: true, niveau_id: '', filiere_id: '', regime: 'NORMAL' });
    setModalOpen(true);
  };

  const handleOpenEdit = (classe) => {
    setModalMode('edit');
    setSelectedItem(classe);
    setFormData({
      name: classe.name || '',
      short: classe.short || '',
      actif: classe.actif !== false,
      niveau_id: classe.niveau_id || '',
      filiere_id: classe.filiere_id || '',
      regime: classe.regime === 'TA' ? 'TA' : 'NORMAL'
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Changer de filiere invalide le niveau choisi : il pouvait appartenir a
    // l ancienne filiere. On le vide pour ne pas enregistrer une incoherence.
    if (name === 'filiere_id') {
      setFormData({ ...formData, filiere_id: value, niveau_id: '' });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Le nom est obligatoire';
    if (!formData.filiere_id) {
      errs.filiere_id = 'La filière est obligatoire : elle détermine la liste des niveaux proposés';
    }
    if (!formData.niveau_id) {
      errs.niveau_id = 'Le niveau est obligatoire, sinon l application devinera et pourra retenir le niveau d une autre année';
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setError(Object.values(errs)[0]);
      return;
    }
    try {
      setSubmitting(true);
      const headers = getAuthHeaders();
      if (modalMode === 'add') {
        const res = await axios.post('/api/classes', formData, { headers });
        const partage = res.data?.partageNiveau;
        setSuccessMessage(
          partage > 0
            ? `Classe créée. ${partage} autre classe porte déjà ce niveau : elles partageront le même programme.`
            : 'Classe créée'
        );
      } else {
        await axios.put(`/api/classes/${selectedItem._id}`, formData, { headers });
        setSuccessMessage('Classe modifiée');
      }
      handleClose();
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (classe) => {
    const cible = String(classe.name);
    if (!window.confirm(`Supprimer la classe « ${cible} » ?\n\nLes classes sans séances programmées uniquement.`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/classes/${classe._id}`, { headers });
      setSuccessMessage(`Classe « ${cible} » supprimée`);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Gestion des classes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Chaque classe est rattachée à un niveau et une filière. Ce lien est obligatoire : il évite
            que l application devine le niveau d après le nom, ce qui affichait les matières de
            l année précédente sur IRM2.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Recharger">
            <IconButton onClick={loadAll}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
            Nouvelle classe
          </Button>
        </Stack>
      </Stack>

      {sansLien > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{sansLien} classe(s) sans niveau rattaché</AlertTitle>
          Tant qu elles ne sont pas rattachées, l application devine leur niveau d après leur nom.
          Utilisez « Modifier » pour choisir le niveau.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher une classe, un niveau, une filière..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
          )
        }}
        sx={{ mb: 2, maxWidth: 480 }}
      />

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Classe</TableCell>
                <TableCell>Sigle</TableCell>
                <TableCell>Régime</TableCell>
                <TableCell>Niveau</TableCell>
                <TableCell>Année</TableCell>
                <TableCell>Filière</TableCell>
                <TableCell>État</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((classe) => {
                  const niveau = getNiveau(classe.niveau_id);
                  const filiere = getFiliere(classe.filiere_id);
                  return (
                    <TableRow key={classe._id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{classe.name}</TableCell>
                      <TableCell>{classe.short || '—'}</TableCell>
                      <TableCell>
                        {classe.regime === 'TA'
                          ? <Chip size="small" color="warning" label="TA" />
                          : <Chip size="small" variant="outlined" label="Normal" />}
                      </TableCell>
                        <TableCell>
                          {niveau ? (
                            <Chip size="small" label={`${niveau.niveau_id} – ${niveau.intitule}`} />
                          ) : classe.niveau_id ? (
                            <Chip size="small" color="warning" icon={<LinkOffIcon />} label={`Non rattaché (${classe.niveau_id})`} />
                          ) : (
                            <Chip size="small" color="warning" icon={<LinkOffIcon />} label="Non rattaché" />
                          )}
                        </TableCell>
                      <TableCell>{niveau?.annee_id ? String(niveau.annee_id) : '—'}</TableCell>
                      <TableCell>{filiere?.libelle || classe.filiere_id || '—'}</TableCell>
                      <TableCell>
                        {classe.actif !== false
                          ? <Chip size="small" color="success" label="Active" />
                          : <Chip size="small" label="Inactive" />}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => handleOpenEdit(classe)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" color="error" onClick={() => handleDelete(classe)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucune classe
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          rowsPerPageOptions={[10, 15, 25, 50]}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? 'Nouvelle classe' : `Modifier « ${selectedItem?.name} »`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              name="name" label="Nom de la classe" value={formData.name} onChange={handleChange}
              placeholder="L3 Développement informatique (G1)"
            />
            <TextField
              name="short" label="Sigle" value={formData.short} onChange={handleChange}
              placeholder="L3 Dev G1" helperText="Laissez vide pour reprendre le nom"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Régime</InputLabel>
              <Select
                name="regime"
                label="Régime"
                value={formData.regime || 'NORMAL'}
                onChange={handleChange}
              >
                <MenuItem value="NORMAL">Normal</MenuItem>
                <MenuItem value="TA">Temps aménagé (TA)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                TA : cours en soirée (19:00-22:00) en semaine et le week-end (09:30-16:30).
              </Typography>
            </FormControl>
            <Divider>Lien obligatoire</Divider>
            <FormControl fullWidth size="small" error={!formData.filiere_id && !!formData.name}>
              <InputLabel>Filière</InputLabel>
              <Select
                name="filiere_id"
                label="Filière"
                value={formData.filiere_id}
                onChange={handleChange}
              >
                {filieresAnneeActive().map((f) => (
                  <MenuItem key={f.filiere_id} value={f.filiere_id}>
                    {f.libelle || f.intitule}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Choisissez la filière d’abord : la liste des niveaux en dépend.
              </Typography>
            </FormControl>
            <FormControl
              fullWidth
              size="small"
              error={!!formData.filiere_id && !formData.niveau_id}
              disabled={!formData.filiere_id}
            >
              <InputLabel>Niveau</InputLabel>
              <Select
                name="niveau_id"
                label="Niveau"
                value={formData.niveau_id}
                onChange={handleChange}
                displayEmpty
              >
                {!formData.filiere_id ? (
                  <MenuItem value="" disabled>
                    <em>Choisissez d’abord une filière</em>
                  </MenuItem>
                ) : (
                  niveauxDeFiliere(formData.filiere_id).map((n) => (
                    <MenuItem key={`${n.niveau_id}-${n.annee_id}`} value={n.niveau_id}>
                      {n.intitule}
                    </MenuItem>
                  ))
                )}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {formData.filiere_id
                  ? 'Le niveau détermine les matières affichées dans l’emploi du temps.'
                  : 'Les niveaux de l’année active uniquement.'}
              </Typography>
            </FormControl>
            {formData.filiere_id && niveauxDeFiliere(formData.filiere_id).length === 0 && (
              <Alert severity="info">
                Cette filière n’a aucun niveau pour l’année active. Créez d’abord le niveau dans
                Paramètres → Niveaux.
              </Alert>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <Groups fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                La filière puis le niveau pilotent le programme ; la classe reste l’unité qui reçoit
                les séances.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} disabled={submitting}>
            {submitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

export default Classes;